/**
 * The article body's ONLY construct: `{{fig:<id>}}` (#1637).
 *
 * ── THE TWO PROPERTIES, AND THEY DO NOT IMPLY EACH OTHER ───────────────────
 * #1372 measured a platform where an external principal's `<script>` reached
 * sealed document output RAW, because template substitution was "unconditional
 * textual replacement with no replacement string and therefore no metacharacter
 * escaping". Two separate rules came out of that, and this file is where the
 * portal holds both:
 *
 *   1. untrusted text must never become MARKUP. This file's answer is
 *      structural: it returns TEXT SEGMENTS, never HTML. React renders a text
 *      segment as a text node, which is escaped by construction, and
 *      `dangerouslySetInnerHTML` appears nowhere in the KB surface. There is no
 *      escaper here on purpose — an escaper implies something is being
 *      concatenated into markup, and nothing is. #1372's SQL escape contract
 *      (`fix/1372-html-escape-boundary`, core 2026083012) is HELD and not on
 *      main; it is also about a different boundary — core's HTML/email/PDF
 *      renderer — so it is neither consumed nor privately re-implemented here.
 *      That is stated rather than left silent (rule 41's last paragraph).
 *
 *   2. untrusted text must never become a TOKEN. That is what this file is
 *      FOR, and it is enforced three ways:
 *        · the grammar is FINITE — `FIGURE_ID` is the same vocabulary the
 *          database's `kb_asset_key_ok` CHECK accepts, so a body can only ever
 *          name an id that could have been registered;
 *        · the scan is ONE PASS with a monotonically advancing cursor. Nothing
 *          this function emits is ever re-read, so a value that looks like a
 *          marker cannot become one (the accumulating-`replace` shape #728
 *          removed from `documents` and #1372 found still live in core);
 *        · resolution is by LOOKUP, never by construction. An id the article's
 *          asset manifest does not contain resolves to a `missing` segment —
 *          it never falls back to printing the marker, and it never becomes a
 *          path.
 *
 * Anything that is not an exact well-formed marker is TEXT. `{{fig:../../x}}`,
 * `{{fig:<script>}}`, `{{FIG:a}}`, `{{ fig:a }}`, `{{{fig:a}}}` and a bare
 * `{{fig:` are all prose, and prose is rendered as a text node. That is the
 * refusal: a malformed marker is inert, visible, and not a token.
 */

/**
 * The finite figure-id vocabulary.
 *
 * Compiled from ONE rule that also lives in SQL, as
 * `service.kb_asset.kb_asset_key_ok`. Two implementations of one finite rule
 * is how the two drift (rule 18), so `modules/service/test/kb-assets.test.ts`
 * asserts the CHECK constraint's source contains this exact character class,
 * and `articleTokens.test.ts` asserts this constant does.
 */
export const FIGURE_ID = '[A-Za-z0-9][A-Za-z0-9._-]{0,63}'

/**
 * Non-global on purpose. A `g` regex carries `lastIndex` across calls, and a
 * module-level one shared between two renders is a classic source of skipped
 * matches. The scan below drives the cursor itself.
 */
const MARKER = new RegExp(`\\{\\{fig:(${FIGURE_ID})\\}\\}`)

export type ArticleSegment =
  /** Prose. Rendered as a React text node — never as markup. */
  | { kind: 'text'; text: string }
  /** A marker that resolved to an asset the caller may read. */
  | { kind: 'figure'; id: string }
  /**
   * A well-formed marker naming an asset this caller's manifest does not hold
   * — unpublished, superseded, or simply not visible to them. It is a
   * RECOVERY state, not a literal: the marker text is deliberately dropped, so
   * the raw token can never appear in rendered output (#1637 acceptance).
   */
  | { kind: 'missing'; id: string }

/**
 * Split an article body into finite segments.
 *
 * @param body   the stored article text, exactly as the door returned it
 * @param known  the figure ids the caller's own asset manifest contains
 */
export function parseArticleBody(
  body: string | null | undefined,
  known: Iterable<string> = [],
): ArticleSegment[] {
  const source = typeof body === 'string' ? body : ''
  if (source === '') return []

  const resolvable = known instanceof Set ? known : new Set(known)
  const out: ArticleSegment[] = []

  // ONE PASS. `cursor` only ever moves forward, and every emitted segment is
  // taken from `source` — never from the accumulator — so nothing this
  // function produces can be rescanned as input.
  let cursor = 0
  for (;;) {
    const rest = source.slice(cursor)
    const m = MARKER.exec(rest)
    if (!m || m.index === undefined) break

    const at = cursor + m.index
    // A marker only counts when the braces around it are EXACT. `{{{fig:a}}}`
    // is prose that happens to contain a marker-shaped run, and treating it as
    // a token would be inventing a triple-brace "trusted" syntax nothing has.
    const exact = source[at - 1] !== '{' && source[at + m[0].length] !== '}'
    if (!exact) {
      // Consume up to and including this run, as TEXT, and keep going. The
      // cursor still advances, so this cannot loop.
      pushText(out, source.slice(cursor, at + m[0].length))
      cursor = at + m[0].length
      continue
    }

    if (at > cursor) pushText(out, source.slice(cursor, at))
    const id = m[1]!
    out.push(resolvable.has(id) ? { kind: 'figure', id } : { kind: 'missing', id })
    cursor = at + m[0].length
  }

  if (cursor < source.length) pushText(out, source.slice(cursor))
  return out
}

/** Adjacent text runs are one paragraph's worth of prose, not two. */
function pushText(out: ArticleSegment[], text: string) {
  if (text === '') return
  const last = out[out.length - 1]
  if (last?.kind === 'text') last.text += text
  else out.push({ kind: 'text', text })
}

/** Every well-formed marker in a body, in order, whether resolvable or not. */
export function figureReferences(body: string | null | undefined): string[] {
  return parseArticleBody(body, [])
    .filter((s): s is Extract<ArticleSegment, { kind: 'missing' }> => s.kind === 'missing')
    .map((s) => s.id)
}

/**
 * Bytes as a person reads them. Used by the document card and the zoom pane.
 * `Intl` rather than a hand-rolled table so a French portal reads "1,2 Mo"-ish
 * separators rather than an English decimal point.
 */
export function formatBytes(bytes: number | null | undefined, locale?: string): string {
  const n = typeof bytes === 'number' && Number.isFinite(bytes) && bytes >= 0 ? bytes : null
  if (n === null) return ''
  if (n < 1000) return `${n} bytes`
  const units = ['kB', 'MB', 'GB']
  let value = n / 1000
  let unit = 0
  while (value >= 1000 && unit < units.length - 1) { value /= 1000; unit += 1 }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 ? 1 : 0 })
    .format(value)} ${units[unit]}`
}

/** The short word a person uses for a format, from its MIME type. */
export function formatLabel(contentType: string | null | undefined): string {
  const t = (contentType ?? '').toLowerCase()
  if (t === 'application/pdf') return 'PDF'
  if (t.startsWith('image/')) return t.slice('image/'.length).toUpperCase()
  return 'File'
}
